<template>
    <div :class="embedded ? 'fila-pendentes-embedded h-100' : 'widget widget-corrections-queue h-100'">
        <div v-if="!embedded" class="widget-heading">
            <h5>{{ title }}</h5>
            <span class="badge badge-primary">{{ items.length }}</span>
        </div>
        <div :class="embedded ? 'fila-pendentes-embedded__content' : 'widget-content'">
            <!-- Esqueleto para fila -->
            <div v-if="loading" class="skeleton-container">
                <div v-for="i in 3" :key="i" class="skeleton-turma">
                    <div class="skeleton-circle"></div>
                    <div class="skeleton-content">
                        <div class="skeleton-line skeleton-line-title"></div>
                        <div class="skeleton-line skeleton-line-info"></div>
                    </div>
                </div>
            </div>
            <div v-else class="corrections-queue">
                <div 
                    v-for="item in items" 
                    :key="item.id" 
                    class="correction-item" 
                    @click="handleItemClick(item)"
                >
                    <div class="correction-avatar">
                        <img 
                            v-if="getItemImage(item)" 
                            :src="getItemImage(item)" 
                            :alt="getItemName(item)" 
                            class="avatar-img"
                        >
                        <span v-else class="avatar-initials">{{ getInitials(getItemName(item)) }}</span>
                    </div>
                    <div class="correction-info">
                        <div class="correction-header">
                            <span class="correction-aluno">{{ getItemName(item) }}</span>
                        </div>
                        <span 
                            class="correction-tema" 
                            :title="getItemSubtitle(item)"
                        >
                            {{ getItemSubtitle(item) }}
                        </span>
                        <div class="correction-meta">
                            <span 
                                class="correction-turma" 
                                :title="getItemMeta(item)"
                            >
                                {{ getItemMeta(item) }}
                            </span>
                        </div>
                    </div>
                    <div class="correction-action">
                        <button class="btn-corrigir" type="button" :title="actionButtonTitle" @click.stop="handleItemClick(item)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-edit">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                        <span class="correction-tempo">{{ getItemTimeValue(item) }}</span>
                    </div>
                </div>
                <!-- Estado vazio rico: título + descrição; botão só se houver onEmptyStateClick (ex.: fila de correções) -->
                <div
                    v-if="items.length === 0 && mostrarEstadoVazioRico"
                    class="empty-state-container"
                >
                    <div class="empty-state-content">
                        <h5 class="empty-state-title">{{ emptyStateTitle }}</h5>
                        <p
                            class="empty-state-description"
                            :class="{ 'empty-state-description--solo': !onEmptyStateClick }"
                        >
                            {{ emptyStateDescription }}
                        </p>
                        <button
                            v-if="onEmptyStateClick"
                            type="button"
                            class="btn btn-primary empty-state-button"
                            @click="onEmptyStateClick"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-plus me-2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            {{ emptyStateButtonText }}
                        </button>
                    </div>
                </div>
                <div v-else-if="items.length === 0" class="empty-queue">
                    <p>{{ emptyMessage }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

// Função para calcular tempo de espera (deve estar antes de defineProps)
const calcularTempoEspera = (dataSubmissao) => {
    if (!dataSubmissao) return 'Data não disponível';
    
    const agora = new Date();
    const submissao = new Date(dataSubmissao);
    const diffInSeconds = Math.floor((agora - submissao) / 1000);
    
    if (diffInSeconds < 60) {
        return 'agora';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
        return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `há ${diffInYears} ${diffInYears === 1 ? 'ano' : 'anos'}`;
};

const props = defineProps({
    /** Quando true, omite o cabeçalho e a casca .widget (use dentro de um card que já tem heading/abas). */
    embedded: {
        type: Boolean,
        default: false,
    },
    actionButtonTitle: {
        type: String,
        default: 'Corrigir redação',
    },
    title: {
        type: String,
        default: '',
    },
    items: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    emptyMessage: {
        type: String,
        default: 'Nenhum item pendente'
    },
    // Estado vazio rico (como Turmas recentes): título, descrição e botão
    emptyStateTitle: {
        type: String,
        default: 'Nenhum item na fila'
    },
    emptyStateDescription: {
        type: String,
        default: 'Envie seu primeiro item para começar.'
    },
    emptyStateButtonText: {
        type: String,
        default: 'Enviar primeiro item'
    },
    onEmptyStateClick: {
        type: Function,
        default: null,
    },
    /** Exibe título + descrição do estado vazio mesmo sem botão (ex.: fila de revisões de texto). */
    emptyStateSemBotao: {
        type: Boolean,
        default: false,
    },
    // Funções para extrair dados do item
    getItemName: {
        type: Function,
        default: (item) => item.nome_aluno || item.titulo || item.nome || 'Sem nome'
    },
    getItemSubtitle: {
        type: Function,
        default: (item) => item.tema_redacao || item.descricao || ''
    },
    getItemMeta: {
        type: Function,
        default: (item) => item.nome_turma || item.curso_nome || ''
    },
    getItemTime: {
        type: Function,
        default: undefined
    },
    getItemImage: {
        type: Function,
        default: (item) => item.imagem_perfil_aluno || item.imagem || null
    },
    // Função de callback quando item é clicado
    onItemClick: {
        type: Function,
        default: null,
    },
});

const mostrarEstadoVazioRico = computed(
    () => Boolean(props.onEmptyStateClick) || props.emptyStateSemBotao
);

// Função para gerar iniciais a partir de um nome
const getInitials = (nome) => {
    if (!nome || typeof nome !== 'string') return '';
    const palavras = nome.trim().split(/\s+/);
    if (palavras.length >= 2) {
        return (palavras[0][0] + palavras[1][0]).toUpperCase();
    } else if (palavras.length === 1 && palavras[0].length > 0) {
        return palavras[0].substring(0, 2).toUpperCase();
    }
    return '';
};

// Função helper para obter o tempo do item
const getItemTimeValue = (item) => {
    if (props.getItemTime) {
        return props.getItemTime(item);
    }
    // Fallback: usar calcularTempoEspera se getItemTime não foi fornecido
    return item.tempoEspera || calcularTempoEspera(item.data_submissao || item.created_at);
};

// Handler para clique no item
const handleItemClick = (item) => {
    if (props.onItemClick) {
        props.onItemClick(item);
    }
};
</script>

<style scoped>
/* Estilos para Fila de Correções - Minimalista */
.fila-pendentes-embedded,
.fila-pendentes-embedded__content {
    min-height: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
}

.widget-corrections-queue {
    min-height: 250px !important;
}

.widget-corrections-queue .widget-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.widget-corrections-queue .widget-heading .badge {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 10px;
}

.widget-corrections-queue .widget-content {
    min-height: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
}

/* Preenche a altura do card; só esta área rola (uma rolagem só) */
.corrections-queue {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 5px;
}

/* Quando só tem estado vazio, preenche a altura para centralizar como Turmas recentes */
.corrections-queue:has(.empty-state-container) {
    overflow: hidden;
}

.corrections-queue::-webkit-scrollbar {
    width: 4px;
}

.corrections-queue::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.corrections-queue::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

.corrections-queue::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

.correction-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #e0e6ed;
    border-radius: 8px;
    transition: all 0.2s ease;
    cursor: pointer;
}

.correction-item:hover {
    border-color: #4361ee;
    background: #f8f9ff;
}

.correction-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}

.correction-avatar .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-initials {
    color: #fff;
    font-weight: 600;
    font-size: 12px;
}

.correction-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.correction-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.correction-aluno {
    font-size: 13px;
    font-weight: 600;
    color: #0e1726;
    line-height: 1.3;
}

.correction-tema {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    cursor: help;
}

.correction-meta {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 11px;
    color: #888ea8;
    min-width: 0;
}

.correction-turma,
.correction-tempo {
    display: flex;
    align-items: center;
}

.correction-turma {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: help;
}

.correction-action {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.correction-action .correction-tempo {
    font-size: 10px;
    color: #888ea8;
    white-space: nowrap;
}

.btn-corrigir {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    background: transparent;
    color: #4361ee;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.btn-corrigir:hover {
    background: #4361ee;
    color: #fff;
    border-color: #4361ee;
}

.btn-corrigir svg {
    width: 14px;
    height: 14px;
}

.empty-queue {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    color: #888ea8;
}

.empty-queue p {
    font-size: 13px;
    color: #888ea8;
    margin: 0;
}

/* Estado vazio rico (embedded e widget; centraliza na área da fila) */
.corrections-queue .empty-state-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    width: 100%;
    padding: 30px 20px;
    text-align: center;
}

.corrections-queue .empty-state-content {
    max-width: 350px;
    width: 100%;
}

.corrections-queue .empty-state-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    line-height: 1.4;
}

.corrections-queue .empty-state-description {
    color: #6b7280;
    font-size: 0.9rem;
    line-height: 1.4;
    margin-bottom: 20px;
}

.corrections-queue .empty-state-description--solo {
    margin-bottom: 0;
}

.corrections-queue .empty-state-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    font-weight: 500;
    font-size: 0.9rem;
    border-radius: 6px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.corrections-queue .empty-state-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

[data-bs-theme="dark"] .corrections-queue .empty-state-title,
.dark .corrections-queue .empty-state-title,
body.dark .corrections-queue .empty-state-title {
    color: #f3f4f6;
}

[data-bs-theme="dark"] .corrections-queue .empty-state-description,
.dark .corrections-queue .empty-state-description,
body.dark .corrections-queue .empty-state-description {
    color: #9ca3af;
}

/* Skeleton preenche a altura do card para não encolher durante o carregamento */
.skeleton-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
}

.skeleton-turma {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
}

.skeleton-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.5s infinite;
    flex-shrink: 0;
}

.skeleton-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-line-title {
    width: 60%;
}

.skeleton-line-info {
    width: 40%;
}

@keyframes skeleton-shimmer {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

/* Modo escuro */
[data-bs-theme="dark"] .correction-item,
.dark .correction-item,
body.dark .correction-item {
    background: #0e1726 !important;
    border-color: #707280 !important;
}

[data-bs-theme="dark"] .correction-item:hover,
.dark .correction-item:hover,
body.dark .correction-item:hover {
    border-color: #4361ee !important;
    background: #1a2332 !important;
}

[data-bs-theme="dark"] .correction-aluno,
.dark .correction-aluno,
body.dark .correction-aluno {
    color: #e0e6ed !important;
}

[data-bs-theme="dark"] .correction-tema,
.dark .correction-tema,
body.dark .correction-tema {
    color: #888ea8 !important;
}

[data-bs-theme="dark"] .correction-turma,
[data-bs-theme="dark"] .correction-tempo,
.dark .correction-turma,
.dark .correction-tempo,
body.dark .correction-turma,
body.dark .correction-tempo {
    color: #888ea8 !important;
}

[data-bs-theme="dark"] .empty-queue p,
.dark .empty-queue p,
body.dark .empty-queue p {
    color: #888ea8 !important;
}

[data-bs-theme="dark"] .skeleton-circle,
[data-bs-theme="dark"] .skeleton-line,
.dark .skeleton-circle,
.dark .skeleton-line,
body.dark .skeleton-circle,
body.dark .skeleton-line {
    background: linear-gradient(90deg, #2d3748 25%, #4a5568 50%, #2d3748 75%);
    background-size: 200% 100%;
}
</style>
